import { GetSessionsAllGameDaysRes } from '@/app/(default)/(leagues)/leagues/api/getSessionsAllGameDays/route';
import { GetCompletedMatchesRes } from '@/app/(default)/(leagues)/leagues/api/getCompletedMatches/route';
import { GetAllScoreFormatsRes } from '@/app/(default)/(leagues)/leagues/api/getScoreFormats/route';
import { GetGroupsContentRes } from '@/app/(default)/(leagues)/leagues/api/getGroupsContent/route';
import { GetMatchesContentRes } from '@/app/(default)/(leagues)/leagues/api/getMatchesContent/route';
import { GetGameDayHeaderRes } from '@/app/(default)/(leagues)/leagues/api/gameDayHeader/route';
import { GetRsvpContentRes } from '@/app/(default)/(leagues)/leagues/api/getRsvpContent/route';
import { useQuery } from '@tanstack/react-query';
import { NEXT_PUBLIC_PL_KEY } from '@/modules/league/new-components/constants';

export const useGetGameDayHeaderQueryData = (
  slug: string,
  sessionId: string,
  matchDateId: string
) => {
  return useQuery({
    queryKey: ['league_game_day_header', slug, sessionId, matchDateId],
    queryFn: async () => {
      try {
        const response = await fetch(
          `/leagues/api/gameDayHeader?slug=${slug}&sessionId=${sessionId}&matchDateId=${matchDateId}`,
          {
            headers: {
              'X-PL-PUBLIC-KEY': `${NEXT_PUBLIC_PL_KEY}`,
            },
          }
        );
        const responseData: { data: GetGameDayHeaderRes } = await response.json();

        return responseData.data;
      } catch {
        return null;
      }
    },
    staleTime: 30 * 1000,
  });
};

export const useGetGroupsQueryData = (
  matchDateId: string,
  isScramble: boolean,
  isPartnerOrSingles: boolean,
  defaultPoolTemplateID: number,
  selectedCourt: number,
  currentSequenceNumber: number,
  isRatingLeague: boolean,
  isLocked: boolean
) => {
  return useQuery({
    queryKey: [
      'league_groups',
      matchDateId,
      isScramble,
      isPartnerOrSingles,
      defaultPoolTemplateID,
      selectedCourt,
      currentSequenceNumber,
      isRatingLeague,
      isLocked,
    ],
    queryFn: async () => {
      try {
        const response = await fetch(
          `/leagues/api/getGroupsContent?matchDateId=${matchDateId}&isScramble=${isScramble}&isPartnerOrSingles=${isPartnerOrSingles}&defaultPoolTemplateID=${defaultPoolTemplateID}&selectedCourt=${selectedCourt}&sequenceNumber=${currentSequenceNumber}&isRatingLeague=${isRatingLeague}&isLocked=${isLocked}`,
          {
            headers: {
              'X-PL-PUBLIC-KEY': `${NEXT_PUBLIC_PL_KEY}`,
            },
          }
        );
        const responseData: { data: GetGroupsContentRes } = await response.json();

        return responseData.data;
      } catch {
        return null;
      }
    },
    staleTime: 30 * 1000,
  });
};

export const useGetMatchesQueryData = (
  matchDateId: string,
  selectedPoolID: string,
  currentSequenceNumber: number,
  isRatingLeague: boolean,
  isMatchStarted: boolean
) => {
  return useQuery({
    queryKey: [
      'league_matches',
      matchDateId,
      selectedPoolID,
      currentSequenceNumber,
      isRatingLeague,
      isMatchStarted,
    ],
    queryFn: async () => {
      try {
        const response = await fetch(
          `/leagues/api/getMatchesContent?matchDateId=${matchDateId}&selectedPoolID=${selectedPoolID}&sequenceNumber=${currentSequenceNumber}&isRatingLeague=${isRatingLeague}&isMatchStarted=${isMatchStarted}`,
          {
            headers: {
              'X-PL-PUBLIC-KEY': `${NEXT_PUBLIC_PL_KEY}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const responseData: { data: GetMatchesContentRes } = await response.json();

        return responseData.data;
      } catch {
        return null;
      }
    },
    staleTime: 15 * 1000,
  });
};

export const useGetRSVPContent = (
  isWaitListActive: boolean,
  matchDateId: string,
  clubId: string,
  filterData: boolean,
  showOptInDateToPublicOnPlayingList: boolean,
  showOptInDateToPublicOnWaitingList: boolean
) => {
  return useQuery({
    queryKey: [
      'league_rsvp',
      isWaitListActive,
      matchDateId,
      clubId,
      filterData,
      showOptInDateToPublicOnPlayingList,
      showOptInDateToPublicOnWaitingList,
    ],
    queryFn: async () => {
      try {
        const response = await fetch(
          `/leagues/api/getRsvpContent?isWaitListActive=${isWaitListActive}&matchDateId=${matchDateId}&clubId=${clubId}&filterData=${filterData}&showOptInDateToPublicOnPlayingList=${showOptInDateToPublicOnPlayingList}&showOptInDateToPublicOnWaitingList=${showOptInDateToPublicOnWaitingList}`,
          {
            headers: {
              'X-PL-PUBLIC-KEY': `${NEXT_PUBLIC_PL_KEY}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const responseData: { data: GetRsvpContentRes } = await response.json();

        return responseData.data;
      } catch {
        return null;
      }
    },
    staleTime: 30 * 1000,
  });
};

export const useGetCompletedMatches = (matchDateId: string, isAuthorized: boolean) => {
  return useQuery({
    queryKey: ['league_change_scores', matchDateId, isAuthorized],
    queryFn: async () => {
      try {
        if (isAuthorized) {
          const response = await fetch(
            `/leagues/api/getCompletedMatches?matchDateId=${matchDateId}`,
            {
              headers: {
                'X-PL-PUBLIC-KEY': `${NEXT_PUBLIC_PL_KEY}`,
              },
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          const responseData: { data: GetCompletedMatchesRes } = await response.json();

          return responseData.data;
        } else {
          return null;
        }
      } catch {
        return null;
      }
    },
    staleTime: 30 * 1000,
  });
};

export const useGetSessionsAllGameDaysQueryData = (leagueId: string) => {
  return useQuery({
    queryKey: ['league_sessions_game_days', leagueId],
    queryFn: async () => {
      try {
        const response = await fetch(`/leagues/api/getSessionsAllGameDays?leagueId=${leagueId}`, {
          headers: {
            'X-PL-PUBLIC-KEY': `${NEXT_PUBLIC_PL_KEY}`,
          },
        });
        const responseData: { data: GetSessionsAllGameDaysRes } = await response.json();

        return responseData.data;
      } catch {
        return null;
      }
    },
    staleTime: 24 * 60 * 60,
  });
};

export const useGetAllScoreFormats = () => {
  return useQuery({
    queryKey: ['league_all_score_formats'],
    queryFn: async () => {
      try {
        const response = await fetch(
          `/leagues/api/getScoreFormats?scoreFormatId=0&moduleFor=League`,
          {
            headers: {
              'X-PL-PUBLIC-KEY': `${NEXT_PUBLIC_PL_KEY}`,
            },
          }
        );
        const responseData: { data: GetAllScoreFormatsRes } = await response.json();

        return responseData.data;
      } catch {
        return null;
      }
    },
    staleTime: 24 * 60 * 60,
  });
};
